import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import { FaEdit } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import axiosInstance from '../../axios';

const LedgerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { full_name } = location.state || {};
  const [ledgers, setLedgers] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editableData, setEditableData] = useState({});

  useEffect(() => {
    axiosInstance.post(`/employee/getAllRecordsByFullname`, {
      full_name: full_name,
    }).then(response => {
      setLedgers(response.data);
    });
  }, [full_name]);

  const exportToExcel = () => {
    const filteredLedgers = ledgers.map(({ id, ...rest }) => rest);
    const worksheet = XLSX.utils.json_to_sheet(filteredLedgers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "LeaveDetails");

    const employeeName = ledgers.length > 0 ? ledgers[0].full_name : 'Employee';
    XLSX.writeFile(workbook, `${employeeName}.xlsx`);
  };

  const handleEditClick = (ledger) => {
    setEditRowId(ledger.id);
    setEditableData(ledger);
  };

  const handleSaveClick = () => {
    axiosInstance.post(`/employee/updatebyId/${editRowId}`, editableData)
      .then(response => {
        setLedgers(ledgers.map(ledger => ledger.id === editRowId ? editableData : ledger));
        setEditRowId(null);
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditableData(prev => {
      const updatedData = { ...prev, [name]: value };
      if (name === "added_leaves" || name === "taken_leaves") {
        updatedData.balance_leaves = updatedData.added_leaves - updatedData.taken_leaves;
      }
      return updatedData;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-full max-w-6xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <ArrowLeftIcon
              className="w-6 h-6 text-gray-600 cursor-pointer hover:text-gray-800 transition"
              onClick={() => navigate('/employee')}
            />
            <h1 className="text-xl md:text-2xl font-semibold text-gray-700">
              {ledgers.length > 0 && ledgers[0].full_name}
            </h1>
          </div>
          <button 
            onClick={exportToExcel} 
            className="bg-blue-600 text-white px-3 py-1.5 md:px-4 md:py-2 rounded hover:bg-blue-700 transition"
          >
            Export to Excel
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md text-sm md:text-base">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="p-2 md:p-4 text-left text-gray-800 font-semibold">Date</th>
                <th className="p-2 md:p-4 text-left text-gray-800 font-semibold">Total Leaves</th>
                <th className="p-2 md:p-4 text-left text-gray-800 font-semibold">Leaves Taken</th>
                <th className="p-2 md:p-4 text-left text-gray-800 font-semibold">Balance Leaves</th>
                <th className="p-2 md:p-4 text-left text-gray-800 font-semibold">Remark</th>
                <th className="p-2 md:p-4 text-left text-gray-800 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ledgers.map((ledger) => (
                <tr key={ledger.id} className="border-b border-gray-200">
                  <td className="p-2 md:p-4 text-gray-600">
                    {editRowId === ledger.id ? (
                      <input
                        type="date"
                        name="date"
                        value={editableData.date}
                        onChange={handleChange}
                        className="border p-1 rounded w-full"
                      />
                    ) : (
                      formatDate(ledger.date)
                    )}
                  </td>
                  <td className="p-2 md:p-4 text-gray-600">
                    {editRowId === ledger.id ? (
                      <input
                        type="number"
                        name="added_leaves"
                        value={editableData.added_leaves || ''}
                        onChange={handleChange}
                        className="border p-1 rounded w-full"
                      />
                    ) : (
                      ledger.added_leaves
                    )}
                  </td>
                  <td className="p-2 md:p-4 text-gray-600">
                    {editRowId === ledger.id ? (
                      <input
                        type="number"
                        name="taken_leaves"
                        value={editableData.taken_leaves || ''}
                        onChange={handleChange}
                        className="border p-1 rounded w-full"
                      />
                    ) : (
                      ledger.taken_leaves
                    )}
                  </td>
                  <td className="p-2 md:p-4 text-gray-600">
                    {editRowId === ledger.id ? (
                      editableData.balance_leaves || ''
                    ) : (
                      ledger.balance_leaves
                    )}
                  </td>
                  <td className="p-2 md:p-4 text-gray-600">
                    {editRowId === ledger.id ? (
                      <input
                        type="text"
                        name="remark"
                        value={editableData.remark || ''}
                        onChange={handleChange}
                        className="border p-1 rounded w-full"
                      />
                    ) : (
                      ledger.remark
                    )}
                  </td>
                  <td className="p-2 md:p-4 text-gray-600">
                    {editRowId === ledger.id ? (
                      <button
                        onClick={handleSaveClick}
                        className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition"
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEditClick(ledger)}
                        className="text-blue-600 hover:text-blue-800 transition"
                      >
                        <FaEdit className="w-5 h-5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LedgerDetails;
